/**
 * Human-readable labels for the system's user roles.
 *
 * Roles are stored as snake_case tokens (`super_admin`, `administrator`, …).
 * The UI must never surface the raw token — a sidebar or badge showing
 * "SUPER_ADMIN" with the underscore reads as a bug. `roleLabel` is the single
 * humanizer every surface uses so the wording stays consistent.
 *
 * The canonical role set (see src/config/enums.js + src/middleware/auth.js):
 *   super_admin · admin · administrator · analyst · agent · client
 */
const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  // The 'admin' role is retired — kept as an alias to "Office Administrator" so
  // any residual/legacy value still reads correctly until it's fully migrated out.
  admin: 'Office Administrator',
  administrator: 'Office Administrator',
  analyst: 'Analyst',
  agent: 'Agent',
  client: 'Client',
};

/** Turn a role token into a display label. Unknown tokens degrade gracefully
 *  (`some_new_role` → "Some New Role") rather than leaking an underscore. */
export function roleLabel(role: string | null | undefined): string {
  if (!role) return '';
  const known = ROLE_LABELS[role];
  if (known) return known;
  return role
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Every role the app knows about, in descending privilege order. 'admin' is
 *  retired (migrated to 'administrator') and no longer listed. */
export const ALL_ROLES = [
  'super_admin',
  'administrator',
  'analyst',
  'agent',
  'client',
] as const;

export type Role = (typeof ALL_ROLES)[number];
